/**
 * Vincent B947 (Eric 2026-02-14 SPEC STRICK PROD) — Bulk Verify Modal.
 *
 * Lance un fact-check en masse sur toutes les synthèses d'une cohorte,
 * d'un parcours ou de toute l'org. Job async — la modal reste ouverte et
 * polle le statut jusqu'à `done`.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X, Warning } from "@phosphor-icons/react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function BulkVerifyModal({ isOpen, onClose, onDone }) {
  const [scope, setScope] = useState("cohort"); // "org" | "cohort" | "pathway"
  const [scopeId, setScopeId] = useState("");
  const [cohorts, setCohorts] = useState([]);
  const [pathways, setPathways] = useState([]);
  const [launching, setLaunching] = useState(false);
  const [jobId, setJobId] = useState(null);
  const [jobStatus, setJobStatus] = useState(null);

  useEffect(() => {
    if (!isOpen) return;
    // Charge la liste de cohortes + parcours pour peupler les selects.
    (async () => {
      try {
        const [c, p] = await Promise.all([
          axios.get(`${API}/api/cohorts`, { withCredentials: true }).catch(() => ({ data: { cohorts: [] } })),
          axios.get(`${API}/api/pathways`, { withCredentials: true }).catch(() => ({ data: { pathways: [] } })),
        ]);
        setCohorts(c.data?.cohorts || c.data || []);
        setPathways(p.data?.pathways || p.data || []);
      } catch { /* silencieux */ }
    })();
  }, [isOpen]);

  // Poll job status
  useEffect(() => {
    if (!jobId) return undefined;
    const t = setInterval(async () => {
      try {
        const { data } = await axios.get(`${API}/api/agent/jobs/${jobId}`, { withCredentials: true });
        setJobStatus(data);
        if (data?.status === "done" || data?.status === "error") {
          clearInterval(t);
          if (data.status === "done") {
            const total = data.result?.total_claims_detected || 0;
            const n = data.result?.syntheses_processed || 0;
            toast.success(`✓ Bulk verify terminé : ${n} synthèse(s) analysée(s), ${total} claim(s) détecté(s).`);
            if (onDone) await onDone();
          } else {
            toast.error(`Erreur job : ${data.error || "inconnu"}`);
          }
        }
      } catch (err) {
        console.error("[bulk-verify] poll error", err);
      }
    }, 5000); // Vincent B955 (Eric 2026-02-14 SPEC STRICK PROD) — Poll espacé de 3s → 5s pour réduire la pression backend (les jobs durent plusieurs minutes, +2s ne dégrade pas l'UX perçue).
    return () => clearInterval(t);
  }, [jobId, onDone]);

  const launch = async () => {
    if (launching) return;
    if (scope !== "org" && !scopeId) {
      toast.error("Sélectionne une cohorte ou un parcours.");
      return;
    }
    setLaunching(true);
    setJobId(null);
    setJobStatus(null);
    try {
      const { data } = await axios.post(
        `${API}/api/admin/factual-corrections/bulk-verify`,
        { scope, scope_id: scopeId || null },
        { withCredentials: true },
      );
      setJobId(data?.job_id);
      toast.success(`Job lancé — ${data?.syntheses_count} synthèse(s) à analyser (provider: ${data?.provider}).`);
    } catch (err) {
      toast.error("Erreur : " + (err?.response?.data?.detail || err.message));
    } finally {
      setLaunching(false);
    }
  };

  if (!isOpen) return null;

  const progress = jobStatus
    ? {
        pct: jobStatus.total_chunks
          ? Math.round(((jobStatus.chunks_processed || 0) / jobStatus.total_chunks) * 100)
          : 0,
        step: jobStatus.current_step,
        status: jobStatus.status,
      }
    : null;

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4"
      data-testid="bulk-verify-modal"
    >
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] flex flex-col">
        <header className="flex items-center justify-between p-4 border-b border-zinc-200">
          <h2 className="text-lg font-semibold text-zinc-900">
            🚀 Vérification factuelle en masse
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="p-1 rounded hover:bg-zinc-100"
            data-testid="bulk-verify-close-btn"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </header>

        <div className="p-4 flex-1 overflow-y-auto space-y-4">
          <div className="bg-amber-50 border border-amber-200 rounded p-3 text-xs text-amber-900 flex gap-2">
            <Warning size={16} className="flex-shrink-0 mt-0.5" />
            <p>
              Chaque synthèse déclenche un appel LLM (Perplexity Sonar Pro si configuré,
              sinon GPT-5.2). Coût estimé : ~0.02-0.05$ CAD par synthèse. Le job tourne
              en arrière-plan — tu peux fermer la modal.
            </p>
          </div>

          <fieldset className="border border-zinc-200 rounded p-3">
            <legend className="text-xs font-semibold text-zinc-700 px-1">Portée</legend>
            <label className="flex items-center gap-2 mb-1.5 cursor-pointer">
              <input
                type="radio"
                name="scope"
                value="cohort"
                checked={scope === "cohort"}
                onChange={() => setScope("cohort")}
                className="accent-teal-600"
                data-testid="bulk-verify-scope-cohort"
              />
              <span className="text-sm">Cohorte</span>
              {scope === "cohort" && (
                <select
                  value={scopeId}
                  onChange={(e) => setScopeId(e.target.value)}
                  className="ml-auto text-sm px-2 py-1 border border-zinc-300 rounded flex-1 max-w-[300px]"
                  data-testid="bulk-verify-cohort-select"
                >
                  <option value="">— Sélectionne une cohorte —</option>
                  {cohorts.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name || c.title || c.id}
                    </option>
                  ))}
                </select>
              )}
            </label>
            <label className="flex items-center gap-2 mb-1.5 cursor-pointer">
              <input
                type="radio"
                name="scope"
                value="pathway"
                checked={scope === "pathway"}
                onChange={() => setScope("pathway")}
                className="accent-teal-600"
                data-testid="bulk-verify-scope-pathway"
              />
              <span className="text-sm">Parcours</span>
              {scope === "pathway" && (
                <select
                  value={scopeId}
                  onChange={(e) => setScopeId(e.target.value)}
                  className="ml-auto text-sm px-2 py-1 border border-zinc-300 rounded flex-1 max-w-[300px]"
                  data-testid="bulk-verify-pathway-select"
                >
                  <option value="">— Sélectionne un parcours —</option>
                  {pathways.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name || p.title || p.id}
                    </option>
                  ))}
                </select>
              )}
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="scope"
                value="org"
                checked={scope === "org"}
                onChange={() => setScope("org")}
                className="accent-teal-600"
                data-testid="bulk-verify-scope-org"
              />
              <span className="text-sm">Toutes les vidéos de l'organisation ⚠️</span>
            </label>
          </fieldset>

          {progress && (
            <div className="bg-blue-50 border border-blue-200 rounded p-3" data-testid="bulk-verify-progress">
              <p className="text-xs font-semibold text-blue-900 mb-1.5">
                {progress.status === "done"
                  ? "✓ Terminé"
                  : progress.status === "error"
                  ? "✗ Erreur"
                  : "⏳ En cours…"}
              </p>
              <div className="w-full bg-blue-100 rounded h-2 overflow-hidden">
                <div
                  className="bg-blue-600 h-full transition-all"
                  style={{ width: `${progress.pct}%` }}
                />
              </div>
              <p className="text-[11px] text-blue-800 mt-1">
                {progress.step} — {progress.pct}%
              </p>
              {progress.status === "done" && jobStatus.result && (
                <p className="text-[11px] text-emerald-800 mt-1" data-testid="bulk-verify-done-summary">
                  ✓ {jobStatus.result.syntheses_processed} synthèse(s), {jobStatus.result.total_claims_detected} claim(s) détecté(s).
                  Ouvre chaque vidéo pour trier / appliquer les corrections via « Vérifier sur le web ».
                </p>
              )}
            </div>
          )}
        </div>

        <footer className="p-4 border-t border-zinc-200 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="text-xs font-semibold px-3 py-1.5 rounded bg-white border border-zinc-300 text-zinc-700 hover:bg-zinc-50"
            data-testid="bulk-verify-cancel-btn"
          >
            Fermer
          </button>
          <button
            type="button"
            onClick={launch}
            disabled={launching || (jobId && jobStatus?.status === "running")}
            className="text-xs font-semibold px-3 py-1.5 rounded bg-teal-600 text-white hover:bg-teal-700 disabled:opacity-50"
            data-testid="bulk-verify-launch-btn"
          >
            {launching
              ? "⏳ Démarrage…"
              : jobId && jobStatus?.status === "running"
              ? "⏳ En cours…"
              : "▶ Lancer la vérification"}
          </button>
        </footer>
      </div>
    </div>
  );
}
